import { useEffect, useRef, type Ref } from "react";
import { UN8_HEART_D, UN8_KNOT, UN8_SIGN_VIEWBOX } from "./unlimitedSignPath";

/** Official sculpture — static sign only. The pointed-heart split is `play`. */
export const UN8_SIGN_SRC = "/brand/UnlimitedSign.png";

/**
 * Official UN8 sign = two same hearts knotted at the waist.
 * Play = draw the 8 → breath → the knot lets go → two hearts fly.
 */
const EASE = "cubic-bezier(.22,1,.36,1)";

function SignHeart({
  flipped,
  pathRef,
}: {
  flipped?: boolean;
  pathRef: Ref<SVGPathElement>;
}) {
  return (
    <svg viewBox={UN8_SIGN_VIEWBOX} fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        ref={pathRef}
        d={UN8_HEART_D}
        fillRule="evenodd"
        transform={
          flipped ? `rotate(180 ${UN8_KNOT.x} ${UN8_KNOT.y})` : undefined
        }
      />
    </svg>
  );
}

export function InfinityHearts({
  play = false,
  className,
}: {
  play?: boolean;
  className?: string;
}) {
  const topWrap = useRef<HTMLDivElement | null>(null);
  const botWrap = useRef<HTMLDivElement | null>(null);
  const topPath = useRef<SVGPathElement | null>(null);
  const botPath = useRef<SVGPathElement | null>(null);

  useEffect(() => {
    if (!play) return;
    const tw = topWrap.current;
    const bw = botWrap.current;
    const tp = topPath.current;
    const bp = botPath.current;
    if (!tw || !bw || !tp || !bp) return;

    const animations: Animation[] = [];
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const draw = (el: SVGPathElement, delay: number, duration: number) => {
      const len = el.getTotalLength();
      el.style.stroke = "currentColor";
      el.style.strokeWidth = "1.6";
      el.style.strokeLinejoin = "round";
      el.style.strokeLinecap = "round";
      el.style.strokeDasharray = `${len}`;
      el.style.strokeDashoffset = `${len}`;
      el.style.fillOpacity = "0";
      animations.push(
        el.animate([{ strokeDashoffset: len }, { strokeDashoffset: 0 }], {
          duration,
          delay,
          easing: "ease-out",
          fill: "forwards",
        }),
        el.animate([{ fillOpacity: 0 }, { fillOpacity: 1 }], {
          duration: 220,
          delay: delay + duration - 40,
          easing: "ease-out",
          fill: "forwards",
        }),
        el.animate([{ strokeWidth: 1.6 }, { strokeWidth: 0 }], {
          duration: 80,
          delay: delay + duration + 160,
          fill: "forwards",
        }),
      );
    };

    draw(tp, 0, 560);
    draw(bp, 90, 560);

    if (reduced) {
      animations.push(
        tw.animate([{ opacity: 1 }, { opacity: 0 }], {
          duration: 200,
          delay: 1180,
          fill: "forwards",
        }),
        bw.animate([{ opacity: 1 }, { opacity: 0 }], {
          duration: 200,
          delay: 1180,
          fill: "forwards",
        }),
      );
      return () => {
        for (const a of animations) a.cancel();
        tp.style.strokeDasharray = "";
        tp.style.strokeDashoffset = "";
        tp.style.stroke = "none";
        tp.style.strokeWidth = "0";
        bp.style.strokeDasharray = "";
        bp.style.strokeDashoffset = "";
        bp.style.stroke = "none";
        bp.style.strokeWidth = "0";
      };
    }

    animations.push(
      tw.animate(
        [
          { transform: "translate(0px, 0px) scale(1)" },
          { transform: "translate(0px, 0px) scale(1.04)" },
          { transform: "translate(0px, 0px) scale(1)" },
        ],
        { duration: 400, delay: 680, easing: "ease-in-out", fill: "forwards" },
      ),
      bw.animate(
        [
          { transform: "translate(0px, 0px) scale(1)" },
          { transform: "translate(0px, 0px) scale(1.04)" },
          { transform: "translate(0px, 0px) scale(1)" },
        ],
        { duration: 400, delay: 680, easing: "ease-in-out", fill: "forwards" },
      ),
    );

    // Knot lets go — hold two complete pointed hearts before they fly.
    animations.push(
      tw.animate(
        [
          { transform: "translate(0px, 0px) scale(1)" },
          { transform: "translate(0px, -48px) scale(1)" },
        ],
        { duration: 520, delay: 1100, easing: EASE, fill: "forwards" },
      ),
      bw.animate(
        [
          { transform: "translate(0px, 0px) scale(1)" },
          { transform: "translate(0px, 48px) scale(1)" },
        ],
        { duration: 520, delay: 1100, easing: EASE, fill: "forwards" },
      ),
    );

    animations.push(
      tw.animate(
        [
          { transform: "translate(0px, -48px) scale(1)", opacity: 1 },
          {
            transform: "translate(-28vw, -28vh) scale(1.45)",
            opacity: 1,
            offset: 0.55,
          },
          {
            transform: "translate(-72vw, -62vh) scale(2.05)",
            opacity: 0,
          },
        ],
        { duration: 820, delay: 1780, easing: EASE, fill: "forwards" },
      ),
      bw.animate(
        [
          { transform: "translate(0px, 48px) scale(1)", opacity: 1 },
          {
            transform: "translate(28vw, 28vh) scale(1.45)",
            opacity: 1,
            offset: 0.55,
          },
          {
            transform: "translate(72vw, 62vh) scale(2.05)",
            opacity: 0,
          },
        ],
        { duration: 820, delay: 1860, easing: EASE, fill: "forwards" },
      ),
    );

    return () => {
      for (const a of animations) a.cancel();
      tw.style.transform = "translate(-72vw, -62vh) scale(2.05)";
      tw.style.opacity = "0";
      bw.style.transform = "translate(72vw, 62vh) scale(2.05)";
      bw.style.opacity = "0";
    };
  }, [play]);

  return (
    <div className={`un8-hearts${className ? ` ${className}` : ""}`} aria-hidden>
      {play ? (
        <>
          <div ref={topWrap} className="un8-heart un8-heart--top">
            <SignHeart pathRef={topPath} />
          </div>
          <div ref={botWrap} className="un8-heart un8-heart--bot">
            <SignHeart flipped pathRef={botPath} />
          </div>
        </>
      ) : (
        <img
          className="un8-hearts__sign"
          src={UN8_SIGN_SRC}
          alt=""
          decoding="async"
        />
      )}
    </div>
  );
}
